import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shared/utils/styling';
import { Loader2 } from 'lucide-react';

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean;
    asChild?: boolean;
  };

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-[length:inherit] font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-0 focus-visible:border-0 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:bg-light-error',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-muted text-secondary-foreground shadow-xs hover:bg-stroke',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        carousel:
          'bg-background text-primary shadow-4xl hover:bg-primary rounded-full hover:text-primary-foreground [&_svg]:size-9 border-none cursor-pointer shadow-[0_0_24px_rgba(24,26,33,0.06),0_24px_48px_rgba(24,26,33,0.06)]',
        icon: 'flex items-center justify-center size-[4.2rem] transition-colors text-primary/70 hover:text-foreground hover:bg-transparent shadow-none bg-transparent  p-0',
      },
      size: {
        default: 'py-[0.8rem] px-[2rem]',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'w-full headline4 px-[3.2rem] py-[1.6rem] h-[5.6rem] rounded-[1.2rem]',
        icon: 'size-9',
        'icon-button': 'size-[4.2rem]',
        small: 'h-[4.2rem] rounded-[1.2rem] px-[1.6rem] py-[1.1rem] headline5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  loading,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  if (loading) {
    return (
      <Comp
        data-slot="button"
        disabled
        className={cn(
          buttonVariants({ variant, size }),
          'flex items-center justify-center',
          className
        )}
        {...props}>
        <Loader2 className="size-8 animate-spin" />
      </Comp>
    );
  }
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
